import {AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {OrdSped} from '../../../_models/OrdSped';
import {Subscription} from 'rxjs';
import {SaveMessage} from '../../../_helpers/save-message';
import {LocalService} from '../../../_services/local.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {OrdinispedizioniService} from '../../../_services/ordinispedizioni.service';
import {Organizzazione} from '../../../_models/Organizzazione';
import {SearchResult} from '../../../_models/SearchResult';
import {skip} from 'rxjs/operators';
import {HubMonitorService} from '../../../_services/hub-monitor.service';

@Component({
    selector: 'app-spedizioni-locali-list',
    templateUrl: './spedizioni-locali-list.component.html',
    styleUrls: ['./spedizioni-locali-list.component.scss'],
    standalone: false
})
export class SpedizioniLocaliListComponent  implements OnInit, OnDestroy, AfterViewInit {
  @Input() updateEnable: boolean;
  @Input() flagTerminate = false;
  @Input() sortKey = 'dataSpedizione';
  @Input() sortDirection = true;
  @Output()  spedizioneSended = new EventEmitter<OrdSped>();
  spedizioni: OrdSped[] = [];
  loading = false;
  page = 0;
  count = 50;
  totalCount = 0;
  loadMore = false;
  search = '';
  selStato = -1;
  numReg = -1;
  codArt = '';
  cliente = '';
  subscription: Subscription;
  private autoLoadObserver: IntersectionObserver | null = null;
  private autoLoadTriggerElement: HTMLElement | null = null;

  @ViewChild('autoLoadTrigger')
  set autoLoadTrigger(ref: ElementRef<HTMLElement> | undefined) {
    this.autoLoadTriggerElement = ref?.nativeElement ?? null;
    void this.observeAutoLoadTrigger();
  }

  constructor(
    private saveMessage: SaveMessage,
    private localService: LocalService,
    private organizzazioneService: OrganizzazioniService,
    private spedizioniService: OrdinispedizioniService,
    private hubMonitorService: HubMonitorService,
    private hostElement: ElementRef<HTMLElement>,
    private ngZone: NgZone) { }

  ngOnInit() {
    this.subscription = this.localService.searchText.subscribe((_value: string) => {
      this.search = _value;
      this.getSpedizioni(_value, false);
    });
    this.subscription.add(this.organizzazioneService.currentOrganizzazioneSubject.pipe(skip(1)).subscribe((_org: Organizzazione) => {
      this.getSpedizioni('',false);
    }));
    this.subscription.add(this.spedizioniService.lastOrdSpedCreated.subscribe((_spedizione: OrdSped) => {
      if (this.shouldIncludeSpedizione(_spedizione)) {
        this.spedizioni.unshift(_spedizione);
        this.totalCount += 1;
        this.loadMore = this.totalCount > this.spedizioni.length;
      }
    }));
    this.subscription.add(this.spedizioniService.lastOrdSpedUpdated.subscribe((_spedizione: OrdSped) => {
      if (_spedizione != null) {
        if (this.flagTerminate && _spedizione.flgTerminato && this.shouldIncludeSpedizione(_spedizione)) {
          this.spedizioni.unshift(_spedizione);
        } else if (_spedizione.flgTerminato) {
          this.excludeSpedizione(_spedizione);
        }
      }
    }));
    this.subscription.add(this.spedizioniService.lastOrdSpedDeleted.subscribe((_spedizione: OrdSped) => {
      if (_spedizione != null) {
        this.excludeSpedizione(_spedizione);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.autoLoadObserver?.disconnect();
  }

  ngAfterViewInit(): void {
    void this.observeAutoLoadTrigger();
  }

  getSpedizioni = (search: string, add: boolean) => {
    this.loading = true;
    this.loadMore = true;
    if (!add) {
      this.page = 0;
    } else {
      this.page += 1;
    }
    return  this.spedizioniService
      .getSpedizioniInviatiFiltered(this.organizzazioneService.organizzazioneId,
        this.flagTerminate, search, this.page, this.count, this.sortKey, this.sortDirection, this.selStato,
        this.numReg, this.codArt, this.cliente)
      .then((_spedizioni: SearchResult<OrdSped>) => {
        this.totalCount = _spedizioni.totalCount;
        if (_spedizioni.totalCount === 0) {
          this.spedizioni = [];
          this.loadMore = false;
          return false;
        }
        if (!add) {
          this.spedizioni = _spedizioni.result;
        } else {
          _spedizioni.result.forEach((spe: OrdSped) => this.spedizioni.push(spe));
        }
        if (_spedizioni.totalCount <= this.spedizioni.length) {
          this.loadMore = false;
        }
      })
      .catch((_err) => this.saveMessage.messageError("Ci sono problemi con il caricamento degli eventi."))
      .finally(() => {
        this.loading = false;
        void this.observeAutoLoadTrigger();
      });
  };

  selectSpedizione = (spedizione: OrdSped) => {
    this.spedizioneSended.emit(spedizione);
  };

  openHubMonitor(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.hubMonitorService.requestOpenHubMonitor();
  }

  private excludeSpedizione(spedizione: OrdSped) {
    this.spedizioni = this.spedizioni.filter((_sped: OrdSped) =>
      !(_sped.idDocumento === spedizione.idDocumento && _sped.idRiga === spedizione.idRiga)
    );
  }

  private shouldIncludeSpedizione(spedizione: OrdSped): boolean {
    return Boolean(spedizione)
      && !this.hasSearch()
      && !this.spedizioni.some((_sped) => _sped.idDocumento === spedizione.idDocumento && _sped.idRiga === spedizione.idRiga)
      && Boolean(spedizione.flgTerminato) === this.flagTerminate
      && this.matchesOptionalNumber(this.selStato, spedizione.statoSpedizione)
      && this.matchesOptionalNumber(this.numReg, spedizione.numRegistraz)
      && this.includesText(spedizione.codArt, this.codArt)
      && this.includesText(`${spedizione.spedRagSoc1 ?? ''} ${spedizione.spedRagSoc2 ?? ''}`, this.cliente);
  }

  private hasSearch(): boolean {
    return Boolean(this.search?.trim());
  }

  private matchesOptionalNumber(filterValue: number, actualValue: number): boolean {
    return filterValue === undefined || filterValue === null || filterValue < 0 || actualValue === filterValue;
  }

  private includesText(actualValue: string, filterValue: string): boolean {
    const filter = filterValue?.trim().toLowerCase();
    if (!filter) {
      return true;
    }

    return (actualValue ?? '').toString().toLowerCase().includes(filter);
  }

  private async observeAutoLoadTrigger(): Promise<void> {
    this.autoLoadObserver?.disconnect();
    if (!this.autoLoadTriggerElement || !this.loadMore) {
      return;
    }

    const ionContent = this.hostElement.nativeElement.closest('ion-content') as HTMLIonContentElement | null;
    const root = ionContent ? await ionContent.getScrollElement().catch(() => null) : null;

    this.autoLoadObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      this.ngZone.run(() => {
        if (this.loadMore && !this.loading) {
          void this.getSpedizioni(this.search, true);
        }
      });
    }, {
      root,
      rootMargin: '0px 0px 320px 0px'
    });

    this.autoLoadObserver.observe(this.autoLoadTriggerElement);
  }
}
